import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface SchedulerStatus {
  killSwitch: { active: boolean; activated_at?: string; activated_by?: string; reason?: string };
  lastHeartbeat: { id: string; ts: string; status: string; events_due: number; events_fired: number; events_failed: number; duration_ms: number | null } | null;
  spend24h: number;
  spendLimit: number;
  pendingCount: number;
  lastUserInput: { ts: string; channel: string } | null;
}

export interface ScheduledEventItem {
  id: string;
  type: string;
  status: string;
  scheduled_at: string;
  title: string;
  payload: Record<string, unknown>;
  outcome: Record<string, unknown> | null;
  created_by: string;
  created_at: string;
  fired_at: string | null;
  recurrence: unknown;
  tags: string[] | null;
}

export interface HeartbeatItem {
  id: string;
  ts: string;
  status: string;
  events_due: number;
  events_fired: number;
  events_failed: number;
  duration_ms: number | null;
  context: unknown;
}

export function useSchedulerStatus() {
  return useQuery<SchedulerStatus>({
    queryKey: ["scheduler-status"],
    queryFn: () => apiFetch<SchedulerStatus>("/api/dev/scheduler/status"),
    refetchInterval: 30_000,
  });
}

export function useSchedulerEvents(status: string = "all") {
  return useQuery<{ items: ScheduledEventItem[]; nextCursor: string | null }>({
    queryKey: ["scheduler-events", status],
    queryFn: () =>
      apiFetch<{ items: ScheduledEventItem[]; nextCursor: string | null }>(
        `/api/dev/scheduler/events?status=${status}&limit=50`
      ),
    refetchInterval: 30_000,
  });
}

export function useSchedulerHeartbeats(limit: number = 20) {
  return useQuery<{ items: HeartbeatItem[] }>({
    queryKey: ["scheduler-heartbeats", limit],
    queryFn: () => apiFetch<{ items: HeartbeatItem[] }>(`/api/dev/scheduler/heartbeats?limit=${limit}`),
    refetchInterval: 30_000,
  });
}

export function useKillScheduler() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>("/api/dev/scheduler/kill", { method: "POST" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["scheduler-status"] }); },
  });
}

export function useResumeScheduler() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>("/api/dev/scheduler/resume", { method: "POST" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["scheduler-status"] }); },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/api/dev/scheduler/cancel/${id}`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduler-events"] });
      queryClient.invalidateQueries({ queryKey: ["scheduler-status"] });
    },
  });
}
